/**
 * Parse a `document.xml` string into a retained DOM + a modeled screenplay.
 * The DOM stays the source of truth for everything except `<paragraphs>`;
 * only the body is lifted into the editable model.
 */
import type { Align, Para, ScreenplayDoc, TextRun } from "./types";
import { parseSettings, parseStyles } from "./styles";

export const OSF_DOCUMENT_TYPE = "Open Screenplay Format document";

/** Thrown when the XML is not a valid Open Screenplay Format document. */
export class OSFFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OSFFormatError";
  }
}

export interface ParsedDocument {
  dom: Document;
  model: ScreenplayDoc;
}

function parseAlign(v: string | null): Align | undefined {
  if (v === "left" || v === "center" || v === "right") return v;
  return undefined;
}

/** Read one `<text>` element into a TextRun, preserving unknown attrs. */
function parseTextRun(el: Element): TextRun {
  const run: TextRun = { text: el.textContent ?? "" };
  let extra: Record<string, string> | undefined;
  const attrs = el.attributes;
  for (let i = 0; i < attrs.length; i++) {
    const a = attrs[i]!;
    switch (a.name) {
      case "bold":
        if (a.value === "1") run.bold = true;
        break;
      case "italic":
        if (a.value === "1") run.italic = true;
        break;
      case "underline":
        if (a.value === "1") run.underline = true;
        break;
      case "font":
        run.font = a.value;
        break;
      default:
        (extra ??= {})[a.name] = a.value;
    }
  }
  if (extra) run.extra = extra;
  return run;
}

/** Read one `<para>` element into a Para. */
function parsePara(paraEl: Element): Para {
  let style = "Normal Text";
  let align: Align | undefined;
  let styleExtra: Record<string, string> | undefined;
  const runs: TextRun[] = [];

  for (const child of Array.from(paraEl.children)) {
    if (child.tagName === "style") {
      style = child.getAttribute("basestyle") ?? style;
      align = parseAlign(child.getAttribute("align"));
      const attrs = child.attributes;
      for (let i = 0; i < attrs.length; i++) {
        const a = attrs[i]!;
        if (a.name !== "basestyle" && a.name !== "align") {
          (styleExtra ??= {})[a.name] = a.value;
        }
      }
    } else if (child.tagName === "text") {
      runs.push(parseTextRun(child));
    }
  }

  const para: Para = { style, runs };
  if (align) para.align = align;

  // Paragraph-level attributes (bookmark + anything unmodeled) → preserve.
  const paraAttrs = paraEl.attributes;
  let extra: Record<string, string> | undefined;
  for (let i = 0; i < paraAttrs.length; i++) {
    const a = paraAttrs[i]!;
    if (a.name === "bookmark") para.bookmark = a.value;
    else (extra ??= {})[a.name] = a.value;
  }
  if (extra) para.extra = extra;
  if (styleExtra) para.styleExtra = styleExtra;
  return para;
}

/** Parse all `<para>` children of a container (paragraphs or titlepage). */
export function parseParas(containerEl: Element): Para[] {
  const paras: Para[] = [];
  for (const child of Array.from(containerEl.children)) {
    if (child.tagName === "para") paras.push(parsePara(child));
  }
  return paras;
}

export function parseDocument(xml: string): ParsedDocument {
  const dom = new DOMParser().parseFromString(xml, "application/xml");

  // Browsers report malformed XML via a <parsererror> element.
  const parserError = dom.getElementsByTagName("parsererror")[0];
  if (parserError) {
    throw new OSFFormatError(
      `Malformed XML: ${parserError.textContent?.trim() ?? "unknown error"}`,
    );
  }

  const root = dom.documentElement;
  if (!root || root.tagName !== "document") {
    throw new OSFFormatError(
      `Root element is <${root?.tagName ?? "?"}>, expected <document>`,
    );
  }
  if (root.getAttribute("type") !== OSF_DOCUMENT_TYPE) {
    throw new OSFFormatError(
      `Not an Open Screenplay Format document (type="${root.getAttribute("type") ?? ""}")`,
    );
  }

  const settingsEl = root.getElementsByTagName("settings")[0];
  const stylesEl = root.getElementsByTagName("styles")[0];
  const paragraphsEl = root.getElementsByTagName("paragraphs")[0];
  if (!settingsEl || !stylesEl || !paragraphsEl) {
    throw new OSFFormatError(
      "Document is missing one of <settings>, <styles>, <paragraphs>",
    );
  }

  const model: ScreenplayDoc = {
    settings: parseSettings(settingsEl),
    styles: parseStyles(stylesEl),
    paras: parseParas(paragraphsEl),
  };

  return { dom, model };
}
